import { promises as fs } from "fs";
import { exec } from "child_process";
import path from "path";
import { resolvePathFromBase } from "../path-utils.js";

export type ExplorerEntryKind = "file" | "directory";
export type ExplorerFileKind = "text" | "image" | "binary";
export type ExplorerEncoding = "utf-8" | "base64" | "none";

export interface ListDirectoryParams {
  root: string;
  relativePath?: string;
}

export interface ReadFileParams {
  root: string;
  relativePath: string;
}

export interface FileExplorerEntry {
  name: string;
  path: string;
  kind: ExplorerEntryKind;
  size: number;
  modifiedAt: string;
}

export interface FileExplorerDirectory {
  path: string;
  entries: FileExplorerEntry[];
}

export interface FileExplorerFile {
  path: string;
  kind: ExplorerFileKind;
  encoding: ExplorerEncoding;
  content?: string;
  mimeType?: string;
  size: number;
  modifiedAt: string;
}

const TEXT_MIME_TYPES: Record<string, string> = {
  ".json": "application/json",
};

const DEFAULT_TEXT_MIME_TYPE = "text/plain";

const IMAGE_MIME_TYPES: Record<string, string> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".svg": "image/svg+xml",
};

const FONT_MIME_TYPES: Record<string, string> = {
  ".ttf": "font/ttf",
  ".otf": "font/otf",
  ".woff": "font/woff",
  ".woff2": "font/woff2",
};

interface ScopedPathParams {
  root: string;
  relativePath?: string;
}

interface EntryPayloadParams {
  root: string;
  targetPath: string;
  name: string;
  kind: ExplorerEntryKind;
}

export async function listDirectoryEntries({
  root,
  relativePath = ".",
}: ListDirectoryParams): Promise<FileExplorerDirectory> {
  const directoryPath = await resolveScopedPath({ root, relativePath });
  const stats = await fs.stat(directoryPath);

  if (!stats.isDirectory()) {
    throw new Error("Requested path is not a directory");
  }

  const dirents = await fs.readdir(directoryPath, { withFileTypes: true });

  const entriesWithNulls = await Promise.all(
    dirents.map(async (dirent) => {
      const targetPath = path.join(directoryPath, dirent.name);
      const kind: ExplorerEntryKind = dirent.isDirectory() ? "directory" : "file";
      try {
        return await buildEntryPayload({
          root,
          targetPath,
          name: dirent.name,
          kind,
        });
      } catch (error) {
        // Directories can contain dangling links (e.g. AGENTS.md -> CLAUDE.md).
        // Skip entries whose targets disappeared instead of failing the whole listing.
        if (isMissingEntryError(error)) {
          return null;
        }
        throw error;
      }
    }),
  );
  const entries = entriesWithNulls.filter((entry): entry is FileExplorerEntry => entry !== null);

  entries.sort((a, b) => {
    const modifiedComparison = new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime();
    if (modifiedComparison !== 0) {
      return modifiedComparison;
    }
    return a.name.localeCompare(b.name);
  });

  return {
    path: normalizeRelativePath({ root, targetPath: directoryPath }),
    entries,
  };
}

export async function readExplorerFile({
  root,
  relativePath,
}: ReadFileParams): Promise<FileExplorerFile> {
  const filePath = await resolveScopedPath({ root, relativePath });
  const stats = await fs.stat(filePath);

  if (!stats.isFile()) {
    throw new Error("Requested path is not a file");
  }

  const ext = path.extname(filePath).toLowerCase();
  const basePayload = {
    path: normalizeRelativePath({ root, targetPath: filePath }),
    size: stats.size,
    modifiedAt: stats.mtime.toISOString(),
  };

  if (ext in IMAGE_MIME_TYPES) {
    const buffer = await fs.readFile(filePath);
    return {
      ...basePayload,
      kind: "image",
      encoding: "base64",
      content: buffer.toString("base64"),
      mimeType: IMAGE_MIME_TYPES[ext],
    };
  }

  const buffer = await fs.readFile(filePath);
  if (isLikelyBinary(buffer)) {
    // Font files: ship base64 bytes so the web client can build a Blob URL
    // for @font-face injection over the relay (no direct-TCP HTTP shortcut).
    if (ext in FONT_MIME_TYPES) {
      return {
        ...basePayload,
        kind: "binary",
        encoding: "base64",
        content: buffer.toString("base64"),
        mimeType: FONT_MIME_TYPES[ext],
      };
    }
    return {
      ...basePayload,
      kind: "binary",
      encoding: "none",
      mimeType: "application/octet-stream",
    };
  }

  return {
    ...basePayload,
    kind: "text",
    encoding: "utf-8",
    content: buffer.toString("utf-8"),
    mimeType: textMimeTypeForExtension(ext),
  };
}

/**
 * Writes UTF-8 text to a file within the given workspace root. Path safety
 * is enforced by `resolveScopedPath` — writes are always scoped to the
 * workspace. Binary file extensions are refused so images/etc. aren't
 * accidentally turned into text blobs.
 *
 * Uses a temp-file + rename for atomicity so partial writes can't corrupt
 * a file mid-edit.
 */
export async function writeTextFile({
  root,
  relativePath,
  content,
}: {
  root: string;
  relativePath: string;
  content: string;
}): Promise<void> {
  const ext = path.extname(relativePath).toLowerCase();
  if (ext in IMAGE_MIME_TYPES) {
    throw new Error(`Refusing to write '${relativePath}': binary/image file`);
  }
  const filePath = await resolveScopedPath({ root, relativePath });
  const tempPath = `${filePath}.${process.pid}.${Date.now()}.tmp`;
  await fs.writeFile(tempPath, content, "utf8");
  await fs.rename(tempPath, filePath);
}

/**
 * Deletes a file inside the given workspace root. Path safety is enforced by
 * `resolveScopedPath` so callers can only delete things inside the workspace.
 *
 * Currently restricted to `.md` files — the only consumer is the Plans modal
 * removing plan markdown files. Tighten/loosen by editing the allowlist if
 * other surfaces need it.
 */
export async function deleteFile({ root, relativePath }: ReadFileParams): Promise<void> {
  const ext = path.extname(relativePath).toLowerCase();
  if (ext !== ".md") {
    throw new Error(`Refusing to delete '${relativePath}': only .md files allowed`);
  }
  const filePath = await resolveScopedPath({ root, relativePath });
  const stats = await fs.stat(filePath);
  if (!stats.isFile()) {
    throw new Error("Requested path is not a file");
  }
  await fs.unlink(filePath);
}

/**
 * Deletes any file or directory inside the given workspace root. Intended for
 * the file-explorer UI where the user can remove arbitrary entries. Path
 * safety is enforced by `resolveScopedPath`. Directories are deleted
 * recursively.
 */
export async function deleteEntry({ root, relativePath }: ReadFileParams): Promise<void> {
  const entryPath = await resolveScopedPath({ root, relativePath });
  await fs.rm(entryPath, { recursive: true, force: false });
}

/**
 * Creates a directory (and any intermediate parents) inside the workspace.
 * Path safety is enforced by `resolveScopedPath`.
 */
export async function createDirectory({ root, relativePath }: ReadFileParams): Promise<void> {
  const dirPath = await resolveScopedPath({ root, relativePath });
  await fs.mkdir(dirPath, { recursive: true });
}

/**
 * Moves (renames) a file or directory within the workspace. Both source and
 * destination are scoped to the workspace root.
 */
export async function moveEntry({
  root,
  sourcePath,
  destinationPath,
}: {
  root: string;
  sourcePath: string;
  destinationPath: string;
}): Promise<void> {
  const src = await resolveScopedPath({ root, relativePath: sourcePath });
  const dest = await resolveScopedPath({ root, relativePath: destinationPath });
  await fs.rename(src, dest);
}

export async function getDownloadableFileInfo({ root, relativePath }: ReadFileParams): Promise<{
  path: string;
  absolutePath: string;
  fileName: string;
  mimeType: string;
  size: number;
}> {
  const filePath = await resolveScopedPath({ root, relativePath });
  const stats = await fs.stat(filePath);

  if (!stats.isFile()) {
    throw new Error("Requested path is not a file");
  }

  const ext = path.extname(filePath).toLowerCase();
  let mimeType = "application/octet-stream";
  if (ext in IMAGE_MIME_TYPES) {
    mimeType = IMAGE_MIME_TYPES[ext] ?? mimeType;
  } else if (ext in FONT_MIME_TYPES) {
    mimeType = FONT_MIME_TYPES[ext] ?? mimeType;
  } else {
    // Read only a small prefix to classify likely text vs binary.
    const handle = await fs.open(filePath, "r");
    const sample = Buffer.alloc(8192);
    try {
      const { bytesRead } = await handle.read(sample, 0, sample.length, 0);
      const chunk = bytesRead < sample.length ? sample.subarray(0, bytesRead) : sample;
      if (!isLikelyBinary(chunk)) {
        mimeType = textMimeTypeForExtension(ext);
      }
    } finally {
      await handle.close();
    }
  }

  return {
    path: normalizeRelativePath({ root, targetPath: filePath }),
    absolutePath: filePath,
    fileName: path.basename(filePath),
    mimeType,
    size: stats.size,
  };
}

async function resolveScopedPath({ root, relativePath = "." }: ScopedPathParams): Promise<string> {
  const normalizedRoot = path.resolve(root);
  const requestedPath = resolvePathFromBase(normalizedRoot, relativePath);
  const relative = path.relative(normalizedRoot, requestedPath);

  if (relative !== "" && (relative.startsWith("..") || path.isAbsolute(relative))) {
    throw new Error("Access outside of workspace is not allowed");
  }

  const realRoot = await fs.realpath(normalizedRoot);

  try {
    const realPath = await fs.realpath(requestedPath);
    const realRelative = path.relative(realRoot, realPath);
    if (realRelative !== "" && (realRelative.startsWith("..") || path.isAbsolute(realRelative))) {
      throw new Error("Access outside of workspace is not allowed");
    }
    return requestedPath;
  } catch (error) {
    if (isMissingEntryError(error)) {
      return requestedPath;
    }
    throw error;
  }
}

async function buildEntryPayload({
  root,
  targetPath,
  name,
  kind,
}: EntryPayloadParams): Promise<FileExplorerEntry> {
  const stats = await fs.stat(targetPath);
  return {
    name,
    path: normalizeRelativePath({ root, targetPath }),
    kind,
    size: stats.size,
    modifiedAt: stats.mtime.toISOString(),
  };
}

function isMissingEntryError(error: unknown): boolean {
  const code = (error as NodeJS.ErrnoException | null)?.code;
  return code === "ENOENT" || code === "ENOTDIR" || code === "ELOOP";
}

function normalizeRelativePath({ root, targetPath }: { root: string; targetPath: string }): string {
  const normalizedRoot = path.resolve(root);
  const normalizedTarget = path.resolve(targetPath);
  const relative = path.relative(normalizedRoot, normalizedTarget);
  return relative === "" ? "." : relative.split(path.sep).join("/");
}

function textMimeTypeForExtension(ext: string): string {
  return TEXT_MIME_TYPES[ext] ?? DEFAULT_TEXT_MIME_TYPE;
}

export interface SearchFilesResult {
  path: string;
  name: string;
  kind: "file" | "directory";
}

const SKIP_DIRS = new Set(["node_modules", ".git", "dist", "build", ".next", ".expo", "out"]);

/**
 * Search for files/dirs whose name contains `query` anywhere in the workspace.
 * Tries `fd` first (respects .gitignore, fast). Falls back to Node recursive walk.
 */
export async function searchWorkspaceFiles({
  root,
  query,
  limit = 200,
}: {
  root: string;
  query: string;
  limit?: number;
}): Promise<SearchFilesResult[]> {
  const normalizedRoot = path.resolve(root);
  const q = query.toLowerCase();

  const fdResult = await tryFdSearch({ root: normalizedRoot, query: q, limit });
  if (fdResult !== null) {
    return fdResult;
  }

  // Node fallback
  const results: SearchFilesResult[] = [];
  const deadline = Date.now() + 500;

  async function walk(dir: string): Promise<void> {
    if (results.length >= limit || Date.now() > deadline) return;
    let entries: import("fs").Dirent[];
    try {
      entries = await fs.readdir(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const entry of entries) {
      if (results.length >= limit || Date.now() > deadline) return;
      if (entry.isDirectory() && SKIP_DIRS.has(entry.name)) continue;
      if (entry.name.toLowerCase().includes(q)) {
        const abs = path.join(dir, entry.name);
        const rel = path.relative(normalizedRoot, abs).split(path.sep).join("/");
        results.push({
          path: rel,
          name: entry.name,
          kind: entry.isDirectory() ? "directory" : "file",
        });
      }
      if (entry.isDirectory()) {
        await walk(path.join(dir, entry.name));
      }
    }
  }

  await walk(normalizedRoot);
  return results;
}

function tryFdSearch({
  root,
  query,
  limit,
}: {
  root: string;
  query: string;
  limit: number;
}): Promise<SearchFilesResult[] | null> {
  return new Promise((resolve) => {
    const safeQuery = query.replace(/[^\w\s\-_.]/g, "");
    if (!safeQuery) {
      resolve(null);
      return;
    }
    const cmd = `fd --max-results ${limit} -i ${JSON.stringify(safeQuery)} ${JSON.stringify(root)}`;
    exec(cmd, { timeout: 500, cwd: root }, (err, stdout) => {
      if (err) {
        resolve(null);
        return;
      }
      const lines = stdout.split("\n").filter(Boolean);
      const results: SearchFilesResult[] = [];
      for (const line of lines) {
        const rel = path.relative(root, line.trim()).split(path.sep).join("/");
        if (!rel || rel.startsWith("..")) continue;
        results.push({ path: rel, name: path.basename(line.trim()), kind: "file" });
      }
      resolve(results);
    });
  });
}

function isLikelyBinary(buffer: Buffer): boolean {
  if (buffer.length === 0) {
    return false;
  }

  let suspicious = 0;
  for (let idx = 0; idx < buffer.length; idx += 1) {
    const byte = buffer[idx];
    if (byte === 0) {
      return true;
    }

    const isControl =
      byte < 32 &&
      byte !== 9 && // tab
      byte !== 10 && // newline
      byte !== 13; // carriage return

    if (isControl || byte === 127) {
      suspicious += 1;
    }
  }

  return suspicious / buffer.length > 0.3;
}
